import React, { useCallback, useEffect, useState } from "react";
import type { Employee } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";

export const EmployeesPage: React.FC = () => {
  const { client } = useAuth();
  const [roleOptions, setRoleOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const fetchEntities = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/entities?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; name: string }>;
      };
      return payload.data.map((item) => ({ id: item.id, label: item.name }));
    },
    [client]
  );

  useEffect(() => {
    let active = true;
    const loadRoles = async () => {
      try {
        const response = await client.get(`/modules/funcoes?take=100&skip=0`);
        if (!response.ok) {
          throw new Error("Falha ao carregar funcoes");
        }
        const payload = (await response.json()) as {
          data: Array<{ id: number; name: string }>;
        };
        if (!active) {
          return;
        }
        setRoleOptions(
          payload.data.map((item) => ({
            value: String(item.id),
            label: item.name,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };
    loadRoles();
    return () => {
      active = false;
    };
  }, [client]);

  return (
    <CrudPage<Employee>
      title="Funcionarios"
      endpoint="/modules/employees"
      searchPlaceholder="Buscar por nome ou documento..."
      columns={[
        { label: "Nome", render: (item) => item.entity?.name ?? item.name },
        { label: "Documento", render: (item) => item.entity?.document ?? item.document ?? "-" },
        { label: "Cargo", render: (item) => item.roleTitle ?? "-" },
        { label: "Funcao", render: (item) => item.roleRef?.name ?? "-" },
        { label: "Departamento", render: (item) => item.department ?? "-" },
        { label: "Status", render: (item) => item.status ?? "-" },
        { label: "Ativo", render: (item) => (item.isActive ? "Sim" : "Nao") },
      ]}
      formFields={[
        {
          name: "entityRefId",
          label: "Entidade",
          type: "autocomplete",
          placeholder: "Buscar entidade",
          fetchOptions: fetchEntities,
          getIdFromItem: (item) => item.entityId,
          getLabelFromItem: (item) => item.entity?.name ?? item.name,
        },
        {
          name: "roleId",
          label: "Funcao",
          type: "select",
          options: roleOptions,
        },
        { name: "roleTitle", label: "Cargo", type: "text" },
        { name: "salary", label: "Salario", type: "text" },
        { name: "hireDate", label: "Data de admissao", type: "date" },
        { name: "department", label: "Departamento", type: "text" },
        { name: "managerName", label: "Gestor", type: "text" },
        {
          name: "workModel",
          label: "Modelo de trabalho",
          type: "select",
          options: [
            { value: "ONSITE", label: "Presencial" },
            { value: "HYBRID", label: "Hibrido" },
            { value: "REMOTE", label: "Remoto" },
          ],
        },
        {
          name: "status",
          label: "Status",
          type: "select",
          options: [
            { value: "ACTIVE", label: "Ativo" },
            { value: "ON_LEAVE", label: "Licenca" },
            { value: "TERMINATED", label: "Desligado" },
          ],
        },
        { name: "admissionDate", label: "Admissao", type: "date" },
        { name: "terminationDate", label: "Desligamento", type: "date" },
        { name: "isActive", label: "Ativo", type: "checkbox" },
      ]}
      initialValues={{
        entityRefId: null,
        roleId: null,
        roleTitle: "",
        salary: "",
        hireDate: "",
        department: "",
        managerName: "",
        workModel: "ONSITE",
        status: "ACTIVE",
        admissionDate: "",
        terminationDate: "",
        isActive: true,
      }}
      mapToPayload={(values) => ({
        entityId: Number(values.entityRefId),
        roleId: values.roleId ? Number(values.roleId) : null,
        roleTitle: values.roleTitle || null,
        salary: values.salary ? Number(values.salary) : null,
        hireDate: values.hireDate || null,
        department: values.department || null,
        managerName: values.managerName || null,
        workModel: values.workModel || null,
        status: values.status || null,
        admissionDate: values.admissionDate || null,
        terminationDate: values.terminationDate || null,
        isActive: values.isActive,
      })}
      permissionWrite="FINANCE_WRITE"
    />
  );
};
